import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Avatar,
  Chip,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Typography,
} from "@mui/material";
import { Sidebar } from "../../containers/Sidebar/Sidebar";
import { CustomTypography } from "../../components/CustomTypography/CustomTypography";
import { CustomTable } from "../../components/CustomTable/CustomTable";
import { CustomButtonTable } from "../../components/CustomButtonTable/CustomButtonTable";
import { CustomLoader } from "../../components/CustomLoader/CustomLoader";
import { Icon } from "../../components/Icon/Icon";
import { colors } from "../../styles/colors";
import {
  Brand,
  BrandType,
  useGetAllBrands,
  useDeleteBrand,
} from "../../api/brand";
import { ModalAddBrand } from "./ModalAddBrand";
import { ModalBrandModels } from "./ModalBrandModels";
import { useServerPagination } from "../../hooks/useServerPagination";
import useStyles from "./styles";

const emptyBrand: Brand = {
  id: -1,
  name: "",
  uid: "",
  type: "auto",
  logo_url: null,
};

type Props = {};

export const Brands: React.FC<Props> = () => {
  const classes = useStyles();
  const { t } = useTranslation();

  const [type, setType] = useState<BrandType>("auto");
  const pagination = useServerPagination();

  const [openAdd, setOpenAdd] = useState(false);
  const [openModels, setOpenModels] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [selected, setSelected] = useState<Brand>(emptyBrand);

  const { brands, totalCount, isPending } = useGetAllBrands(
    type,
    pagination.page,
    pagination.perPage,
    pagination.search
  );
  const deleteBrand = useDeleteBrand();

  const handleChangeType = (e: SelectChangeEvent<BrandType>) => {
    setType(e.target.value as BrandType);
    pagination.resetToFirstPage();
  };

  const openEdit = (brand: Brand) => {
    setSelected(brand);
    setOpenAdd(true);
  };
  const openManageModels = (brand: Brand) => {
    setSelected(brand);
    setOpenModels(true);
  };
  const openConfirmDelete = (brand: Brand) => {
    setSelected(brand);
    setOpenDelete(true);
  };

  const closeAll = () => {
    setOpenAdd(false);
    setOpenModels(false);
    setOpenDelete(false);
    setSelected(emptyBrand);
  };

  const confirmDelete = () => {
    if (selected.id === -1) return;
    deleteBrand.mutate(selected.id, { onSuccess: closeAll });
  };

  const columns = [
    {
      header: t("brand.logo", "Logo"),
      field: "logo_url",
      render: (data: Record<string, any>) => (
        <Avatar
          src={data.logo_url || undefined}
          variant="rounded"
          className={classes.logoCell}
        >
          {!data.logo_url && (data.name?.[0] || "B")}
        </Avatar>
      ),
    },
    { header: t("brand.name", "Nom"), field: "name" },
    {
      header: t("brand.type", "Type"),
      field: "type",
      render: (data: Record<string, any>) => (
        <Chip
          size="small"
          label={data.type || "auto"}
          sx={{
            backgroundColor:
              data.type === "moto" ? colors.secondary : colors.primary,
            color: colors.WHITE,
            textTransform: "uppercase",
            fontWeight: 600,
            fontSize: "11px",
          }}
        />
      ),
    },
    { header: t("brand.uid", "Code"), field: "uid" },
    {
      header: t("brand.models", "Modèles"),
      field: "models",
      render: (data: Record<string, any>) => (
        <CustomButtonTable
          variant="primary"
          textVariant="link"
          label={t("brand.manageModels", "Gérer")}
          width="fit-content"
          fontWeight="bold"
          textDecoration="none"
          iconName="car"
          padding="0px 12px"
          textTransform="none"
          onClick={() => openManageModels(data as Brand)}
        />
      ),
    },
    {
      header: t("common.actions", "Actions"),
      field: "actions",
      render: (data: Record<string, any>) => (
        <Box className={classes.rowActions}>
          <IconButton size="small" onClick={() => openEdit(data as Brand)}>
            <Icon name="pencil" width="18px" height="18px" color={colors.greydark} />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => openConfirmDelete(data as Brand)}
          >
            <Icon name="trash" width="18px" height="18px" color={colors.error} />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <>
      {(isPending || deleteBrand.isPending) && (
        <CustomLoader isActive={isPending || deleteBrand.isPending} />
      )}

      <ModalAddBrand
        open={openAdd}
        brandInitial={selected}
        onCancel={closeAll}
        onClose={closeAll}
      />
      <ModalBrandModels open={openModels} brand={selected} onClose={closeAll} />

      {/* Delete confirmation */}
      <Dialog open={openDelete} onClose={closeAll} maxWidth="sm" fullWidth>
        <DialogTitle>{t("brand.deleteTitle", "Supprimer la marque")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t(
              "brand.deleteConfirm",
              "Êtes-vous sûr de vouloir supprimer cette marque ? Ses modèles seront également supprimés. Cette action est irréversible."
            )}
          </DialogContentText>
          {selected.name && (
            <Typography variant="body1" fontWeight="bold" mt={2}>
              {selected.name}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAll} disabled={deleteBrand.isPending}>
            {t("common.cancel", "Annuler")}
          </Button>
          <Button
            onClick={confirmDelete}
            variant="contained"
            color="error"
            disabled={deleteBrand.isPending}
          >
            {t("common.delete", "Supprimer")}
          </Button>
        </DialogActions>
      </Dialog>

      <Sidebar
        styles={{
          sidebarRightRoot: classes.brandsRoot,
          sidebarRoot: classes.contentMain,
        }}
      >
        <Box className={classes.contentWrapper}>
          <div className={classes.containerHeader}>
            <div className={classes.divIconArrow}>
              <Box className={classes.iconCircle}>
                <Icon name="car" width="28px" height="28px" color={colors.WHITE} />
              </Box>
              <CustomTypography
                variant="h2"
                text={t("brand.brandManagement", "Gestion des marques")}
                isUppercase
              />
            </div>

            <Box className={classes.addButton}>
              <CustomButtonTable
                variant="secondary"
                textVariant="link"
                label={t("brand.addBrand", "Ajouter une marque")}
                width="fit-content"
                fontWeight="bold"
                textDecoration="none"
                iconName="plus"
                padding="10px 20px"
                textTransform="uppercase"
                onClick={() => {
                  setSelected(emptyBrand);
                  setOpenAdd(true);
                }}
              />
            </Box>
          </div>

          {/* Type filter */}
          <Box className={classes.filterBar}>
            <FormControl size="small" className={classes.filterControl}>
              <InputLabel id="brand-type-label">
                {t("brand.type", "Type")}
              </InputLabel>
              <Select
                labelId="brand-type-label"
                value={type}
                label={t("brand.type", "Type")}
                onChange={handleChangeType}
              >
                <MenuItem value="auto">{t("brand.auto", "Auto")}</MenuItem>
                <MenuItem value="moto">{t("brand.moto", "Moto")}</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Box className={classes.tableContainer}>
            <CustomTable
              isFilterFromBackend
              columns={columns}
              data={brands}
              isSearchable
              tableMaxHeight="520px"
              titleHeader={t("brand.listOfBrands", "Liste des marques")}
              tablePagnationCount={totalCount}
              {...pagination.tableProps}
            />
          </Box>
        </Box>
      </Sidebar>
    </>
  );
};
